import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler } from '@app/store/actions';
import { basicService, vaultPasswordsService } from '@app/services';
import { IPasswordDetailsNoPwd, IPasswordTypeUIDetails } from '@app/models';
import VaultPasswordComponent from '../components/vaultPassword.component';
import {
    communityPassword,
    snmpAuthPassword,
    snmpPrivacyPassword,
    getPasswordTypeUIDetails,
    hasMachineName,
    showGenericCredentials,
    genericErrorString,
    getShareablePasswordLink,
    getActiveVaultPermission
} from '@app/helpers';
import { CopyComponent, PasslyButton } from '@app/components';
import { Link, HelpOutline } from '@material-ui/icons';
import { Popover, IconButton, Box, Typography } from '@material-ui/core';
import { vaultUpdate } from '@app/store/constants';

export interface IReceivedData {
    passwordId: string;
    vaultId: string;
    revealPassword?: boolean;
    vaultName?: string;
    isIndividual?: boolean;
}

export const PasswordDetailsDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const userVaultsPermissions = useSelector((state: any) => state.signIn.currentUser.vaultPermissions);
    const permissions = userVaultsPermissions ? getActiveVaultPermission(userVaultsPermissions[props.vaultId]) : new Set<string>();
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [passwordDetails, setPasswordDetails] = useState<IPasswordDetailsNoPwd | undefined>(undefined);
    const [passwordType, setPasswordType] = useState<string>('');
    const [passwordTypeIcon, setPasswordIcon] = useState<string>('');
    const [showMachineName, setShowMachineName] = useState<boolean>(false);
    const [showGenericCreds, setShowGenericCreds] = useState<boolean>(false);
    const [syncSourceText, setSyncSourceText] = useState<string>('');
    const [shareableLink, setShareableLink] = useState<string>('');
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [openPasswordSharingHelper, setOpenPasswordSharingHelper] = useState<boolean>(false);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        vaultPasswordsService.getVaultPasswordByPasswordIdNoPwd(props.vaultId, props.passwordId, handleError, handleSuccess);
    }, [props.passwordId]);

    const handleSuccess = (response: any) => {
        const value: IPasswordDetailsNoPwd = response.data;
        setPasswordDetails(value);

        fixPasswordType(value.PasswordType);
        showTheMachineName(value.PasswordType);
        showGenericUsernamePwd(value.PasswordType);
        fixSyncSource(value.AgentProfileId);
        setShareableLink(getShareablePasswordLink(props.passwordId));
    };

    const fixSyncSource = (syncSource: string) => {
        let source: string = translation('SYNC_SOURCE_LOCAL');
        if (syncSource !== null) {
            source = translation('SYNCED');
        }

        setSyncSourceText(source);
    };

    const showGenericUsernamePwd = (passwordEnum: string) => {
        setShowGenericCreds(showGenericCredentials(passwordEnum));
    };

    const showTheMachineName = (passwordEnum: string) => {
        setShowMachineName(hasMachineName(passwordEnum));
    };

    const fixPasswordType = (passwordEnum: string) => {
        const typeDetails: IPasswordTypeUIDetails = getPasswordTypeUIDetails(passwordEnum);

        setPasswordType(translation(typeDetails.passwordTypeForDisplay));
        setPasswordIcon(typeDetails.passwordTypeImage);
    };

    const handleError = (error: any) => {
        setErrorMessage(genericErrorString(error));
    };

    const handleOpenPasswordSharingHelper = (event: React.MouseEvent<HTMLButtonElement>) => {
        setOpenPasswordSharingHelper(true);
        setAnchorEl(event.currentTarget);
    };

    const handleModalCloseCallback = () => {
        setAnchorEl(null);
        setOpenPasswordSharingHelper(false);
    };

    const goToEdit = () => {
        if (props.vaultName) {
            basicService.redirect(
                `/passwordserver/vaults/${props.vaultId}/editpassword/${props.passwordId}?vaultName=${encodeURIComponent(
                    props.vaultName
                )}&isIndividual=${props.isIndividual}`
            );
        }
    };

    return (
        <>
            <Popover
                open={openPasswordSharingHelper}
                anchorEl={anchorEl}
                onClose={handleModalCloseCallback}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'left'
                }}
                getContentAnchorEl={null}
            >
                <Box padding={2}>
                    <Typography variant='body1'>{translation('VAULT_PASSWORD_ENABLE_SHARING_HELP')}</Typography>
                </Box>
            </Popover>

            {passwordDetails && (
                <div className='passwordVaults__wrapper'>
                    <div className='passwordVaults__username-header'>
                        <span className={`vault__passwords-ico ${passwordTypeIcon}`} title={passwordType} />

                        {showGenericCreds && (
                            <>
                                <div className='passwordVaults__username-header-subhead'>
                                    <span className='passwordVaults__username-header-subhead-name'>{translation('VAULT_PASSWORD_USERNAME')}:</span>
                                    <span>{passwordDetails.Username}</span>
                                </div>
                            </>
                        )}

                        {passwordDetails.PasswordType === 'SNMPV3' && (
                            <>
                                <div className='passwordVaults__username-header-subhead'>
                                    <span className='passwordVaults__username-header-subhead-name'>{translation('VAULT_PASSWORD_SNMP_USER')}:</span>
                                    <span>{passwordDetails.SnmpUser}</span>
                                </div>
                            </>
                        )}
                    </div>
                    <div className='passwordVaults vaults__table-passwords'>
                        {showGenericCreds && (
                            <>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_PASSWORD')}:</span>
                                    <span className='vaults__table-passwords--password'>
                                        <VaultPasswordComponent
                                            vaultId={passwordDetails.VaultId}
                                            passwordId={passwordDetails.Id}
                                            revealPassword={props.revealPassword}
                                        />
                                    </span>
                                </div>
                            </>
                        )}

                        {passwordDetails.PasswordType === 'AD_Windows' && (
                            <div className='vaults__table-passwords--wrapper'>
                                <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_DOMAIN')}:</span>
                                <span className='vaults__table-passwords--description'>{passwordDetails.Domain}</span>
                            </div>
                        )}

                        {passwordDetails.PasswordType === 'Web' && (
                            <div className='vaults__table-passwords--wrapper'>
                                <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_WEB')}:</span>
                                <span className='vaults__table-passwords--description'>
                                    <a id='goToWebsite' href={passwordDetails.WebsiteUrl} target='_blank'>
                                        {passwordDetails.WebsiteUrl}
                                    </a>
                                </span>
                            </div>
                        )}

                        {passwordDetails.PasswordType === 'Database' && (
                            <div className='vaults__table-passwords--wrapper'>
                                <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_DB_TYPE')}:</span>
                                {passwordDetails.DBType === 'Other' ? (
                                    <span className='vaults__table-passwords--description'>{passwordDetails.DBTypeOther}</span>
                                ) : (
                                    <span className='vaults__table-passwords--description'>{passwordDetails.DBType}</span>
                                )}
                            </div>
                        )}

                        {showMachineName && (
                            <div className='vaults__table-passwords--wrapper'>
                                <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_MACHINE_NAME')}:</span>
                                <span className='vaults__table-passwords--description'>{passwordDetails.MachineName}</span>
                            </div>
                        )}

                        {passwordDetails.PasswordType === 'SNMP' && (
                            <>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_COMMUNITY_STRING')}:</span>
                                    <span className='vaults__table-passwords--password'>
                                        <VaultPasswordComponent
                                            vaultId={passwordDetails.VaultId}
                                            passwordId={passwordDetails.Id}
                                            passwordWanted={communityPassword}
                                            revealPassword={props.revealPassword}
                                        />
                                    </span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_AGENT_PORT')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpAgentPort}</span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>
                                        {translation('VAULT_PASSWORD_SNMP_AUTHENTICATION_PROFILE')}:
                                    </span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpAuthenticationProfile}</span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_ENCRYPTION_PROTOCOL')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpEncryptionProtocol}</span>
                                </div>
                            </>
                        )}

                        {passwordDetails.PasswordType === 'SNMPV3' && (
                            <>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>
                                        {translation('VAULT_PASSWORD_SNMP_AUTHENTICATION_PASSWORD')}:
                                    </span>
                                    <span className='vaults__table-passwords--password'>
                                        <VaultPasswordComponent
                                            vaultId={passwordDetails.VaultId}
                                            passwordId={passwordDetails.Id}
                                            passwordWanted={snmpAuthPassword}
                                            revealPassword={props.revealPassword}
                                        />
                                    </span>
                                </div>
                                <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--pwd'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_PRIVACY_PASSWORD')}:</span>
                                    <span className='vaults__table-passwords--password'>
                                        <VaultPasswordComponent
                                            vaultId={passwordDetails.VaultId}
                                            passwordId={passwordDetails.Id}
                                            passwordWanted={snmpPrivacyPassword}
                                            revealPassword={props.revealPassword}
                                        />
                                    </span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_AGENT_PORT')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpAgentPort}</span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>
                                        {translation('VAULT_PASSWORD_SNMP_AUTHENTICATION_PROFILE')}:
                                    </span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpAuthenticationProfile}</span>
                                </div>
                                <div className='vaults__table-passwords--wrapper'>
                                    <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_SNMP_ENCRYPTION_PROTOCOL')}:</span>
                                    <span className='vaults__table-passwords--description'>{passwordDetails.SnmpEncryptionProtocol}</span>
                                </div>
                            </>
                        )}

                        <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--long-text'>
                            <span className='vaults__table-passwords--name'>{translation('DESCRIPTION')}:</span>
                            <span className='vaults__table-passwords--description vaults__table-passwords--description-text'>
                                {passwordDetails.Description}
                            </span>
                        </div>

                        <div className='vaults__table-passwords--wrapper'>
                            <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_TYPE')}:</span>
                            <span className='vaults__table-passwords--description'>{passwordType}</span>
                        </div>
                        <div className='vaults__table-passwords--wrapper'>
                            <span className='vaults__table-passwords--name'>{translation('SYNC_SOURCE')}:</span>
                            <span className='vaults__table-passwords--description'>{syncSourceText}</span>
                        </div>
                        <div className='vaults__table-passwords--wrapper'>
                            <span className='vaults__table-passwords--name'>{translation('EXPIRE_AFTER_PREVIEW')}:</span>
                            {passwordDetails.ExpireAfterXMinutes > 0 ? (
                                <span className='vaults__table-passwords--description'>
                                    {translation('IN')} {passwordDetails.ExpireAfterXMinutes} {translation('LOCKOUT_TIME')}
                                </span>
                            ) : (
                                <span className='vaults__table-passwords--description'>{translation('NA')}</span>
                            )}
                        </div>
                        <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper--long-text'>
                            <span className='vaults__table-passwords--name'>{translation('VAULT_PASSWORD_NOTES')}:</span>
                            <span className='vaults__table-passwords--description vaults__table-passwords--description-text main__show-white-space'>
                                {passwordDetails.Notes}
                            </span>
                        </div>
                        {passwordDetails.EnableTrustedThirdPartyAccess && (
                            <div className='vaults__table-passwords--wrapper'>
                                <span className='vaults__table-passwords--name'>
                                    {translation('SHARE_PASSWORD')}&nbsp;
                                    <IconButton size='small' style={{ padding: '0px' }} onClick={handleOpenPasswordSharingHelper}>
                                        <HelpOutline color='disabled' fontSize='small' style={{ padding: '0px' }} />
                                    </IconButton>
                                    :
                                </span>
                                <span className='vaults__table-passwords--description'>
                                    <CopyComponent tooltip={shareableLink} valueToCopy={shareableLink}>
                                        <span>
                                            <PasslyButton variant='text' color='primary'>
                                                <Link color='inherit' />
                                                &nbsp;{translation('VAULT_PASSWORD_COPY_LINK')}
                                            </PasslyButton>
                                        </span>
                                    </CopyComponent>
                                </span>
                            </div>
                        )}
                        {props.vaultName && permissions?.has(vaultUpdate) && (
                            <div className='vaults__table-passwords--wrapper vaults__table-passwords--wrapper-button'>
                                <PasslyButton variant='contained' color='primary' onClick={goToEdit}>
                                    {translation('GO_TO_EDIT')}
                                </PasslyButton>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {errorMessage !== undefined && errorMessage !== '' && <div className='modal__error'>{errorMessage}</div>}
        </>
    );
};
